import type { CSSProperties } from 'react';
import type { HuecoCliente, MiCita, MotivoSinHueco, PreguntaCita, RespuestaPedida, ServicioPedidoCliente, ServicioPublico } from '../api/ops/citasCliente';
import type { EstadoChip } from '../componentes/Chip';

/**
 * Lógica sin estilos de «Pedir cita» (9.9), «Mis citas» y el enlace de la cita (9.10): fechas locales del negocio (las da
 * la API como `local_date` y `local_time`, nunca se convierten), la marca del negocio (G618), las respuestas a las
 * preguntas y los textos que dependen del estado. Nada de dinero: los importes llegan hechos de la API.
 */

// ---- Fechas locales (YYYY-MM-DD, sin zona) ----
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const DIA3 = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MES3 = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];

function partes(fecha: string): Date {
    const [a = 1970, m = 1, d = 1] = fecha.split('-').map(Number);
    return new Date(a, m - 1, d);
}
/** Inicial para el avatar del profesional. */
export const inicialDe = (nombre: string): string => (nombre.trim().charAt(0) || '·').toUpperCase();
export const mayuscula = (s: string): string => (s === '' ? s : s.charAt(0).toUpperCase() + s.slice(1));
/** «jueves 22 de octubre». */
export function diaLargo(fecha: string): string {
    const d = partes(fecha);
    return `${DIAS[d.getDay()] ?? ''} ${String(d.getDate())} de ${MESES[d.getMonth()] ?? ''}`;
}
/** «jueves 22». */
export function diaYNumero(fecha: string): string {
    const d = partes(fecha);
    return `${DIAS[d.getDay()] ?? ''} ${String(d.getDate())}`;
}
/** «jue 22 oct». */
export function diaCorto(fecha: string): string {
    const d = partes(fecha);
    return `${DIA3[d.getDay()] ?? ''} ${String(d.getDate())} ${MES3[d.getMonth()] ?? ''}`;
}
/** Para la baldosa de la fila: día de la semana, número y mes. */
export function baldosaDia(fecha: string): { semana: string; dia: number; mes: string } {
    const d = partes(fecha);
    return { semana: DIA3[d.getDay()] ?? '', dia: d.getDate(), mes: MES3[d.getMonth()] ?? '' };
}
/** Hoy en el navegador (YYYY-MM-DD): solo para el mínimo del selector de día; la antelación la decide la API. */
export function hoyLocal(ahora: Date = new Date()): string {
    return `${String(ahora.getFullYear())}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;
}
/** «1 h 30 min». */
export function duracion(min: number): string {
    if (min < 60) return `${String(min)} min`;
    const resto = min % 60;
    return `${String(Math.floor(min / 60))} h${resto === 0 ? '' : ` ${String(resto)} min`}`;
}
/** Hora de un instante ISO en la zona del negocio («17:00»). */
export function horaEn(iso: string, zona?: string): string {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', ...(zona === undefined ? {} : { timeZone: zona }) });
}
/** «jueves 21 de octubre, 17:00» de un instante ISO en la zona del negocio. */
export function momentoEn(iso: string, zona?: string): string {
    const opciones: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', ...(zona === undefined ? {} : { timeZone: zona }) };
    return `${new Date(iso).toLocaleDateString('es-ES', opciones)}, ${horaEn(iso, zona)}`;
}
/** Nombre de la zona para «Horas de Madrid» (`Europe/Madrid` → «Madrid»). */
export const ciudadDeZona = (zona: string): string => (zona.split('/').pop() ?? zona).replace(/_/g, ' ');

// ---- Marca del negocio (G618, ADR-0173): sin el negro ni la ola de Reservaria ----
const HEX = /^#?[0-9A-Fa-f]{6}$/;
export const esColor = (v: string | null | undefined): v is string => typeof v === 'string' && HEX.test(v);
function luminancia(hex: string): number {
    const h = hex.replace('#', '');
    const c = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
    return 0.2126 * (c[0] ?? 0) + 0.7152 * (c[1] ?? 0) + 0.0722 * (c[2] ?? 0);
}
/** Texto blanco sobre el acento si el contraste con blanco es de 4,5 o más (como 11.8 y 13.8); si no, tinta. */
export const blancoSobre = (hex: string): boolean => 1.05 / (luminancia(hex) + 0.05) >= 4.5;
function rgba(hex: string, a: number): string {
    const h = hex.replace('#', '');
    return `rgba(${String(parseInt(h.slice(0, 2), 16))}, ${String(parseInt(h.slice(2, 4), 16))}, ${String(parseInt(h.slice(4, 6), 16))}, ${String(a)})`;
}
export type TemaMarca = 'light' | 'dark' | 'auto';
/** Lee `?tema=` (claro, oscuro, auto; también en inglés). */
export function temaDe(v: string | null | undefined): TemaMarca | null {
    const t = ({ claro: 'light', light: 'light', oscuro: 'dark', dark: 'dark', auto: 'auto' } as Record<string, TemaMarca>)[v ?? ''];
    return t ?? null;
}
/** Las variables de color de la página con la marca: el acento en lugar del rojo y, en oscuro, el fondo y la tinta. */
export function estiloDeMarca(color: string, oscuro: boolean): CSSProperties {
    const hex = `#${color.replace('#', '').toUpperCase()}`;
    const base: Record<string, string> = {
        '--rv-red': hex,
        '--rv-red-hover': hex,
        '--rv-red-soft': rgba(hex, oscuro ? 0.24 : 0.12),
        '--pc-on': blancoSobre(hex) ? '#FFFFFF' : '#111111',
    };
    if (oscuro) {
        Object.assign(base, {
            '--rv-ink': '#FFFFFF',
            '--rv-fg-1': '#FFFFFF',
            '--rv-fg-2': 'rgba(255,255,255,0.78)',
            '--rv-fg-3': 'rgba(255,255,255,0.58)',
            '--rv-rule': 'rgba(255,255,255,0.12)',
            '--rv-rule-strong': 'rgba(255,255,255,0.28)',
            '--rv-paper': 'var(--rv-graphite)',
            '--rv-bone': '#161616',
            '--pc-card': 'var(--rv-charcoal)',
            '--pc-inv': '#111111',
            '--rv-warning-soft': 'rgba(244,211,94,0.16)',
            '--rv-warning-ink': '#F4D35E',
            '--pc-logo': 'brightness(0) invert(1)',
        });
    }
    return base;
}

// ---- Servicios y respuestas (paso 1) ----
export interface Respuesta {
    opciones?: string[];
    numero?: number;
    texto?: string;
}
export type Respuestas = Readonly<Record<string, Respuesta>>;

/** Lo que se manda a `quoteAppointment`, `searchAppointmentSlots` y `bookAppointment`: los servicios en el orden elegido. */
export function serviciosPedidos(elegidos: readonly string[], catalogo: readonly ServicioPublico[], respuestas: Respuestas): ServicioPedidoCliente[] {
    return elegidos.map((id) => {
        const s = catalogo.find((x) => x.id === id);
        const answers = (s?.questions ?? []).flatMap((q): RespuestaPedida[] => {
            const r = respuestas[q.id];
            if (r === undefined) return [];
            if ((q.kind === 'single_choice' || q.kind === 'multiple_choice') && (r.opciones ?? []).length > 0) return [{ question_id: q.id, option_ids: r.opciones ?? [] }];
            if (q.kind === 'number' && r.numero !== undefined && r.numero > 0) return [{ question_id: q.id, number_value: r.numero }];
            if (q.kind === 'text' && (r.texto ?? '').trim() !== '') return [{ question_id: q.id, text_value: (r.texto ?? '').trim() }];
            return [];
        });
        return answers.length === 0 ? { service_id: id } : { service_id: id, answers };
    });
}

/** Preguntas obligatorias de una elección sin responder (las de opción única; las demás tienen su valor por defecto). */
export function faltanPorResponder(elegidos: readonly string[], catalogo: readonly ServicioPublico[], respuestas: Respuestas): string[] {
    return elegidos.flatMap((id) =>
        (catalogo.find((x) => x.id === id)?.questions ?? [])
            .filter((q) => q.required && (q.kind === 'single_choice' || q.kind === 'multiple_choice') && (respuestas[q.id]?.opciones ?? []).length === 0)
            .map((q) => q.id),
    );
}

/** El orden de las preguntas (`position`). */
export const ordenadas = (qs: readonly PreguntaCita[]): PreguntaCita[] => [...qs].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/** ¿Alguna respuesta suma precio? Entonces el precio del catálogo es «desde». */
export const conDesde = (s: ServicioPublico): boolean =>
    s.questions.some((q) => q.kind === 'number' ? (q.price_per_unit?.amount ?? 0) > 0 : (q.options ?? []).some((o) => o.price_delta.amount > 0));

/** Las categorías en el orden en que llegan, con sus servicios. */
export function porCategoria(servicios: readonly ServicioPublico[]): { nombre: string; servicios: ServicioPublico[] }[] {
    const grupos: { nombre: string; servicios: ServicioPublico[] }[] = [];
    for (const s of servicios) {
        const nombre = s.category ?? '';
        const g = grupos.find((x) => x.nombre === nombre);
        if (g === undefined) grupos.push({ nombre, servicios: [s] });
        else g.servicios.push(s);
    }
    return grupos;
}

// ---- Huecos (paso 2 y «Cambiar») ----
/** Mañana (antes de las 14:00) y Tarde, como RV Huecos con grupos. */
export function gruposDeHuecos(huecos: readonly HuecoCliente[]): { grupo: 'Mañana' | 'Tarde'; huecos: HuecoCliente[] }[] {
    const manana = huecos.filter((h) => h.local_time < '14:00');
    const tarde = huecos.filter((h) => h.local_time >= '14:00');
    return [
        ...(manana.length > 0 ? [{ grupo: 'Mañana' as const, huecos: manana }] : []),
        ...(tarde.length > 0 ? [{ grupo: 'Tarde' as const, huecos: tarde }] : []),
    ];
}

/** Por qué no hay hueco ese día (`unavailable_reason`), con el texto del dibujo. */
export function textoSinHueco(motivo: MotivoSinHueco | undefined, dia: string, negocio: string, minutos: number, profesional: string | null): { titulo: string; texto: string } {
    switch (motivo) {
        case 'closed':
            return { titulo: `${mayuscula(diaLargo(dia))} está cerrado`, texto: `${negocio} no abre ese día.` };
        case 'outside_window':
            return { titulo: 'Ese día ya no se puede', texto: 'Ese día está fuera de las fechas en que se puede pedir cita.' };
        case 'no_professional':
            return { titulo: 'Nadie hace todo esto junto', texto: 'Ningún profesional hace todos estos servicios. Pídelos en dos citas.' };
        case 'hold_expired':
            return { titulo: 'Se ha liberado la hora', texto: 'Elige otra hora.' };
        default:
            return { titulo: `No queda hueco el ${diaLargo(dia)}`, texto: `Ese día no hay ${duracion(minutos)} seguidos libres${profesional === null ? '' : ` con ${profesional}`}.` };
    }
}

// ---- La cita del cliente (9.10) ----
export const ESTADO_CITA: Readonly<Record<MiCita['status'], { texto: string; chip: EstadoChip }>> = {
    pending_payment: { texto: 'Pendiente de pago', chip: 'pending' },
    confirmed: { texto: 'Confirmada', chip: 'confirmed' },
    arrived: { texto: 'Has llegado', chip: 'live' },
    pending_completion: { texto: 'Terminada', chip: 'neutral' },
    completed: { texto: 'Completada', chip: 'confirmed' },
    no_show: { texto: 'No presentado', chip: 'problem' },
    cancelled: { texto: 'Cancelada', chip: 'problem' },
    expired: { texto: 'Caducada', chip: 'problem' },
};
const ESTADO_FIRMA: Readonly<Partial<Record<MiCita['consent_status'], { texto: string; chip: EstadoChip }>>> = {
    requested: { texto: 'Firma pendiente', chip: 'pending' },
    pending: { texto: 'Firmar en el centro', chip: 'pending' },
    signed: { texto: 'Consentimiento firmado', chip: 'confirmed' },
};
export const viva = (c: MiCita): boolean => c.status === 'confirmed' || c.status === 'pending_payment';
/** Las etiquetas de una cita: su estado (salvo las normales) y la firma que falta (solo en las vivas). */
export function chipsDeCita(c: MiCita): { texto: string; chip: EstadoChip }[] {
    const normal = c.status === 'confirmed' || c.status === 'completed' || c.status === 'arrived' || c.status === 'pending_completion';
    const firma = ESTADO_FIRMA[c.consent_status];
    return [...(normal ? [] : [ESTADO_CITA[c.status]]), ...(firma !== undefined && viva(c) && c.consent_status !== 'signed' ? [firma] : [])];
}
export const nombreServicios = (c: MiCita): string => c.services.map((s) => s.name).join(' y ');
export const minutosDe = (c: MiCita): number => c.services.reduce((t, s) => t + (s.duration_minutes ?? 0), 0);
/** El profesional (el de la primera parte: en una cita todos los servicios van con la misma persona). */
export const profesionalDe = (c: MiCita): string | null => c.services.find((s) => s.professional_display_name !== undefined)?.professional_display_name ?? null;

/** ¿Es una ruta de pedir cita? `/pedir-cita/{slug}` (la web) o `/pedir-cita?clave=` (el widget). */
/** El canal de la confirmación (`MyAppointment.confirmation_channel`, G870, G884) como se dice: «por WhatsApp». */
export const NOMBRE_CANAL: Readonly<Record<NonNullable<MiCita['confirmation_channel']>, string>> = { whatsapp: 'WhatsApp', instagram: 'Instagram', telegram: 'Telegram', email: 'email' };

/** «Te mandamos la confirmación por WhatsApp, …» si la API dice el canal; si no, el texto sin canal (G870). */
export const textoConfirmacion = (c: MiCita): string =>
    `Te mandamos la confirmación${c.confirmation_channel == null ? '' : ` por ${NOMBRE_CANAL[c.confirmation_channel]}`}, con el enlace para cambiarla o cancelarla y tu pase.`;

export const RUTA_PEDIR_CITA = '/pedir-cita';
export const RUTA_MIS_CITAS = '/mis-citas';
export const RUTA_ENLACE_CITA = '/cita';
/** Donde se guarda la cita recién pedida mientras el cliente paga la señal (la vuelta de Stripe la vuelve a leer). */
export const CLAVE_CITA_PEDIDA = 'reservaria.cita.pedida';
